import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/console/AuthContext";

const NAV = [
  { to: "/practice", label: "Practice" },
  { to: "/framework", label: "Framework" },
  { to: "/lifecycle", label: "Lifecycle" },
  { to: "/perspective", label: "Perspective" },
  { to: "/engagements", label: "Engagements" },
  { to: "/about", label: "About" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <header
      className={`sticky top-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-ink-0/85 backdrop-blur-xl border-b border-white/[0.08]" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="container-edge flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center group" data-testid="nav-logo-link">
          <img
            src="/assets/logo/v2e-logo-dark-transparent.png"
            alt="Voice2Evidence"
            className="h-8 md:h-9 w-auto group-hover:opacity-80 transition-opacity"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${
                  isActive ? "text-rust" : "text-parchment/70 hover:text-parchment"
                }`
              }
              data-testid={`nav-link-${n.label.toLowerCase()}`}
            >
              {n.label}
            </NavLink>
          ))}
          <Link to="/contact" className="btn-accent" data-testid="nav-cta-request-briefing">
            Request a Briefing
          </Link>
          {user && (
            <Link
              to="/console/engagements"
              className="font-mono text-[11px] uppercase tracking-[0.2em] text-parchment hover:text-rust transition-colors"
              data-testid="nav-console"
              title={`Signed in as ${user.email} · Open workspace`}
            >
              Console
            </Link>
          )}
        </nav>

        <button
          className="md:hidden p-2 -mr-2 text-parchment"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-ink-1 border-t border-white/[0.08]" data-testid="nav-mobile-panel">
          <div className="container-edge py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className="font-mono text-xs uppercase tracking-[0.2em] text-parchment"
                data-testid={`nav-mobile-link-${n.label.toLowerCase()}`}
              >
                {n.label}
              </NavLink>
            ))}
            <Link to="/contact" className="btn-accent mt-2" data-testid="nav-mobile-cta">
              Request a Briefing
            </Link>
            {user && (
              <Link
                to="/console/engagements"
                className="font-mono text-[11px] uppercase tracking-[0.2em] text-parchment hover:text-rust transition-colors mt-2"
                data-testid="nav-mobile-console"
              >
                Console
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
